// NEW: UI_REVAMP
import { Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { getProfileCreditTransactionApi } from '../../api/query/profileApi';
import DesktopPagination, {
	buildCompactPaginationItems,
	buildPaginationItems,
} from '../common/DesktopPagination';
import { TransactionHistoryRow } from './TransactionRows';
import useIsMobileView from '../../hooks/useIsMobileView';

const TRANSACTION_PAGE_SIZE = 3;

const TransactionHistory = ({
	ticketStatus,
	ticketNumber = '',
	dateFrom,
	dateTo,
	emptyState,
	onHasDataChange,
	onFetchingChange,
}) => {
	const [desktopPageNum, setDesktopPageNum] = useState(1);
	const [desktopPageSize, setDesktopPageSize] = useState(TRANSACTION_PAGE_SIZE);
	const [mobilePageNum, setMobilePageNum] = useState(1);
	const [mobilePageSize, setMobilePageSize] = useState(TRANSACTION_PAGE_SIZE);
	const isMobileView = useIsMobileView();

	const formattedDateFrom = dateFrom ? dayjs(dateFrom).format('YYYY-MM-DD HH:mm:ss') : '';
	const formattedDateTo = dateTo ? dayjs(dateTo).format('YYYY-MM-DD HH:mm:ss') : '';
	const isTicketNumberSearch = Boolean(ticketNumber.trim());
	const requestDateFrom = isTicketNumberSearch ? '' : formattedDateFrom;
	const requestDateTo = isTicketNumberSearch ? '' : formattedDateTo;
	const requestTransactionType = isTicketNumberSearch ? 'ALL' : ticketStatus;
	const userId = sessionStorage.getItem('userId');

	useEffect(() => {
		setDesktopPageNum(1);
	}, [dateFrom, dateTo, ticketNumber, ticketStatus, desktopPageSize]);

	useEffect(() => {
		setMobilePageNum(1);
	}, [dateFrom, dateTo, ticketNumber, ticketStatus, mobilePageSize]);

	const getDesktopProfileCreditTransaction = useQuery(
		[
			'getProfileCreditTransactionApi',
			'desktop',
			requestDateFrom,
			requestDateTo,
			ticketNumber,
			requestTransactionType,
			desktopPageNum,
			desktopPageSize,
		],
		() =>
			getProfileCreditTransactionApi({
				dateFrom: requestDateFrom,
				dateTo: requestDateTo,
				userId,
				pageSize: desktopPageSize,
				pageParam: desktopPageNum,
				transactionType: requestTransactionType,
				ticketNumber,
			}),
		{
			enabled: !isMobileView && Boolean(formattedDateFrom) && Boolean(formattedDateTo),
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);

	const getMobileProfileCreditTransaction = useQuery(
		[
			'getProfileCreditTransactionApi',
			'mobile',
			requestDateFrom,
			requestDateTo,
			ticketNumber,
			requestTransactionType,
			mobilePageNum,
			mobilePageSize,
		],
		() =>
			getProfileCreditTransactionApi({
				dateFrom: requestDateFrom,
				dateTo: requestDateTo,
				userId,
				pageSize: mobilePageSize,
				pageParam: mobilePageNum,
				transactionType: requestTransactionType,
				ticketNumber,
			}),
		{
			enabled: isMobileView && Boolean(formattedDateFrom) && Boolean(formattedDateTo),
			keepPreviousData: true,
			refetchOnWindowFocus: false,
		},
	);
	const desktopRows = flattenTransactionDetails(getDesktopProfileCreditTransaction.data?.content);
	const mobileRows = flattenTransactionDetails(getMobileProfileCreditTransaction.data?.content);
	const activeRows = isMobileView ? mobileRows : desktopRows;
	const isActiveQueryLoading = isMobileView
		? getMobileProfileCreditTransaction.isLoading
		: getDesktopProfileCreditTransaction.isLoading;
	const isActiveQueryFetching = isMobileView
		? getMobileProfileCreditTransaction.isFetching
		: getDesktopProfileCreditTransaction.isFetching;

	useEffect(() => {
		if (!dateFrom || !dateTo || isActiveQueryLoading) return;

		onHasDataChange?.(activeRows.length > 0);
	}, [activeRows.length, dateFrom, dateTo, isActiveQueryLoading, onHasDataChange]);

	useEffect(() => {
		onFetchingChange?.(isActiveQueryFetching);
	}, [isActiveQueryFetching, onFetchingChange]);

	useEffect(() => () => onFetchingChange?.(false), [onFetchingChange]);

	if (!dateFrom || !dateTo) return null;

	const desktopPagination = getPaginationState({
		pageNum: desktopPageNum,
		pageSize: desktopPageSize,
		paging: getDesktopProfileCreditTransaction.data?.paging,
		fallbackTotalEntries: desktopRows.length,
	});

	const mobilePagination = getPaginationState({
		pageNum: mobilePageNum,
		pageSize: mobilePageSize,
		paging: getMobileProfileCreditTransaction.data?.paging,
		fallbackTotalEntries: mobileRows.length,
	});
	if (isActiveQueryLoading) return null;

	return isMobileView ? (
		<TransactionList
			rows={mobileRows}
			pagination={mobilePagination}
			pageNum={mobilePageNum}
			pageSize={mobilePageSize}
			isFetching={getMobileProfileCreditTransaction.isFetching}
			onPageChange={setMobilePageNum}
			onPageSizeChange={setMobilePageSize}
			emptyState={emptyState}
			isMobile
		/>
	) : (
		<TransactionList
			rows={desktopRows}
			pagination={desktopPagination}
			pageNum={desktopPageNum}
			pageSize={desktopPageSize}
			isFetching={getDesktopProfileCreditTransaction.isFetching}
			onPageChange={setDesktopPageNum}
			onPageSizeChange={setDesktopPageSize}
			emptyState={emptyState}
		/>
	);
};

export default TransactionHistory;

const TransactionList = ({
	rows,
	pagination,
	pageNum,
	pageSize,
	isFetching,
	onPageChange,
	onPageSizeChange,
	emptyState,
	isMobile = false,
}) => {
	if (rows.length <= 0) return emptyState || null;

	const paginationItems = isMobile
		? buildCompactPaginationItems(pageNum, pagination.totalPages, 3)
		: buildPaginationItems(pageNum, pagination.totalPages);

	return (
		<Flex flexDirection='column'>
			{rows.map((item, index) => (
				<TransactionHistoryRow
					key={`${item.refNo || 'transaction'}-${item.date}-${index}`}
					date={item.date}
					details={`${item.description}`}
					debit={item.decreaseAmount}
					refNo={item.refNo === '' ? '-' : item.refNo}
					credit={item.increaseAmount}
					isMobileView={isMobile}
				/>
			))}
			<DesktopPagination
				pageNum={pageNum}
				pageSize={pageSize}
				totalEntries={pagination.totalEntries}
				startEntry={pagination.startEntry}
				endEntry={pagination.endEntry}
				totalPages={pagination.totalPages}
				paginationItems={paginationItems}
				isFetching={isFetching}
				onPageChange={onPageChange}
				onPageSizeChange={onPageSizeChange}
			/>
		</Flex>
	);
};

const flattenTransactionDetails = (content = []) => content.flatMap((items) => items.details || []);

const getPaginationState = ({ pageNum, pageSize, paging = {}, fallbackTotalEntries = 0 }) => {
	const totalPages = Math.max(paging?.totalPages || 1, 1);
	const totalEntries =
		paging?.totalElements ||
		paging?.totalCount ||
		paging?.totalRecords ||
		paging?.totalItems ||
		fallbackTotalEntries;
	const startEntry = totalEntries ? (pageNum - 1) * pageSize + 1 : 0;
	const endEntry = totalEntries ? Math.min(pageNum * pageSize, totalEntries) : 0;

	return {
		totalPages,
		totalEntries,
		startEntry,
		endEntry,
	};
};
